import { VariantProps } from "class-variance-authority";
import { Disc } from "lucide-react";
import { forwardRef } from "react";
import { toast } from "sonner";

import { useIsModerator } from "@/features/participants/api/is-moderator";
import {
  useStartRecording,
  useStopRecording,
} from "@/features/recordings/api/toggle-recording";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { ActionButton, ActionButtonProps } from "../action-button";

type RecordCallProps = Omit<ActionButtonProps, "Icon" | "text"> &
  VariantProps<typeof ActionButton> & {
    variant?: "default" | "ghost" | "destructive";
    size?: "fixed" | "auto";
  };

export const RecordCall = forwardRef<HTMLButtonElement, RecordCallProps>(
  ({ variant = "default", size = "fixed", ...props }, ref) => {
    const sessionId = useVidyoStore((state) => state.sessionId);
    const isModerator = useIsModerator();
    const isRecording = useVidyoStore((state) => state.isRecording);
    const { mutateAsync: startRecording, isPending: isStartingRecording } =
      useStartRecording();
    const { mutateAsync: stopRecording, isPending: isStoppingRecording } =
      useStopRecording();

    const isLoading = isStartingRecording || isStoppingRecording;
    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      try {
        if (isRecording) {
          await stopRecording({ sessionId });
        } else {
          await startRecording({ sessionId });
        }
      } catch {
        toast.error(
          isRecording
            ? "Failed to stop recording"
            : "Failed to start recording",
        );
      }

      // Call the original onClick handler if it exists,
      // this closes the dropdown menu
      props.onClick?.(event);
    };

    if (!isModerator) {
      return null;
    }

    return (
      <ActionButton
        {...props}
        ref={ref}
        variant={variant}
        size={size}
        onClick={handleClick}
        Icon={Disc}
        isActive={isRecording}
        disabled={isLoading}
        text={isRecording ? "Stop Recording" : "Record"}
      />
    );
  },
);

RecordCall.displayName = "RecordCall";
